/**
 * NURBS kernel — tensor-product B-spline surface math (R1).
 *
 * The surface is the tensor product of two clamped B-spline curve spaces;
 * the control net is cv[v][u] (rows = V, cols = U). Evaluation, partials,
 * isocurves, boundary edges, point inversion (Newton) and trim-aware
 * tessellation all live here. Trim loops are parameter-space polygons
 * carried as data: the tessellator applies them to the display cache and
 * the cut operations (TrimSrf/Untrim) mutate them.
 */

import type { Pt3 } from '../../commands/geometryMath';
import type { NurbsSurfaceData, NurbsCurveData, TrimLoopData } from './types';
import { surfaceDomains } from './types';
import { basisFunctions, findSpan, interpolateCurve, curveFromControlPoints } from './curve';

/** Weight of a CV (1 when non-rational). */
export function surfaceWeight(data: NurbsSurfaceData, v: number, u: number): number {
    const w = data.weights?.[v]?.[u];
    return typeof w === 'number' && Number.isFinite(w) && w > 0 ? w : 1;
}

/** Evaluate the surface at (u,v) (A5.1 tensor product). */
export function evaluateSurface(data: NurbsSurfaceData, u: number, v: number): Pt3 {
    const { degreeU, degreeV, knotsU, knotsV, cv } = data;
    const nU = cv[0].length - 1, nV = cv.length - 1;
    const uu = Math.min(Math.max(u, knotsU[degreeU]), knotsU[nU + 1]);
    const vv = Math.min(Math.max(v, knotsV[degreeV]), knotsV[nV + 1]);
    const spanU = findSpan(nU, degreeU, uu, knotsU);
    const spanV = findSpan(nV, degreeV, vv, knotsV);
    const Nu = basisFunctions(spanU, degreeU, uu, knotsU);
    const Nv = basisFunctions(spanV, degreeV, vv, knotsV);
    let x = 0, y = 0, z = 0, w = 0;
    for (let j = 0; j <= degreeV; j++) {
        const vi = spanV - degreeV + j;
        if (vi < 0 || vi > nV) continue;
        for (let i = 0; i <= degreeU; i++) {
            const ui = spanU - degreeU + i;
            if (ui < 0 || ui > nU) continue;
            const wi = surfaceWeight(data, vi, ui) * Nu[i] * Nv[j];
            const p = cv[vi][ui];
            x += wi * p[0]; y += wi * p[1]; z += wi * (p[2] ?? 0); w += wi;
        }
    }
    if (w === 0) return [x, y, z];
    return [x / w, y / w, z / w];
}

/** Surface point + first partials + unit normal (central differences). */
export function surfaceDerivatives(data: NurbsSurfaceData, u: number, v: number) {
    const { u: uDom, v: vDom } = surfaceDomains(data);
    const hu = Math.max((uDom[1] - uDom[0]) * 1e-3, 1e-6);
    const hv = Math.max((vDom[1] - vDom[0]) * 1e-3, 1e-6);
    const uc = (x: number) => Math.min(Math.max(x, uDom[0]), uDom[1]);
    const vc = (x: number) => Math.min(Math.max(x, vDom[0]), vDom[1]);
    const p = evaluateSurface(data, u, v);
    const up = evaluateSurface(data, uc(u + hu), v);
    const un = evaluateSurface(data, uc(u - hu), v);
    const vp = evaluateSurface(data, u, vc(v + hv));
    const vn = evaluateSurface(data, u, vc(v - hv));
    const Su: Pt3 = [(up[0] - un[0]) / (2 * hu), (up[1] - un[1]) / (2 * hu), (up[2] - un[2]) / (2 * hu)];
    const Sv: Pt3 = [(vp[0] - vn[0]) / (2 * hv), (vp[1] - vn[1]) / (2 * hv), (vp[2] - vn[2]) / (2 * hv)];
    const cross: Pt3 = [
        Su[1] * Sv[2] - Su[2] * Sv[1],
        Su[2] * Sv[0] - Su[0] * Sv[2],
        Su[0] * Sv[1] - Su[1] * Sv[0],
    ];
    const l = Math.hypot(cross[0], cross[1], cross[2]) || 1;
    return { point: p, Su, Sv, normal: [cross[0] / l, cross[1] / l, cross[2] / l] as Pt3 };
}

/** Surface normal at (u,v). */
export function surfaceNormal(data: NurbsSurfaceData, u: number, v: number): Pt3 {
    return surfaceDerivatives(data, u, v).normal;
}

/**
 * Point inversion: find (u,v) minimizing |S(u,v) − p|. Coarse grid scan +
 * Gauss-Newton iterations on the normal equations.
 */
export function invertSurface(
    data: NurbsSurfaceData, p: Pt3, seed?: { u: number; v: number }, grid = 10
): { u: number; v: number; point: Pt3; dist: number } {
    const { u: [u0, u1], v: [v0, v1] } = surfaceDomains(data);
    let bu = seed?.u ?? (u0 + u1) / 2, bv = seed?.v ?? (v0 + v1) / 2;
    let bd = Infinity;
    if (!seed) {
        for (let i = 0; i <= grid; i++) {
            for (let j = 0; j <= grid; j++) {
                const u = u0 + ((u1 - u0) * i) / grid;
                const v = v0 + ((v1 - v0) * j) / grid;
                const q = evaluateSurface(data, u, v);
                const d = Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]);
                if (d < bd) { bd = d; bu = u; bv = v; }
            }
        }
    }
    const eps = 1e-10;
    for (let it = 0; it < 24; it++) {
        const { point, Su, Sv } = surfaceDerivatives(data, bu, bv);
        const r: Pt3 = [point[0] - p[0], point[1] - p[1], point[2] - p[2]];
        const e11 = Su[0] * Su[0] + Su[1] * Su[1] + Su[2] * Su[2];
        const e12 = Su[0] * Sv[0] + Su[1] * Sv[1] + Su[2] * Sv[2];
        const e22 = Sv[0] * Sv[0] + Sv[1] * Sv[1] + Sv[2] * Sv[2];
        const g1 = Su[0] * r[0] + Su[1] * r[1] + Su[2] * r[2];
        const g2 = Sv[0] * r[0] + Sv[1] * r[1] + Sv[2] * r[2];
        const det = e11 * e22 - e12 * e12;
        if (Math.abs(det) < eps) break;
        const du = (-g1 * e22 + g2 * e12) / det;
        const dv = (-g2 * e11 + g1 * e12) / det;
        const nu = Math.min(Math.max(bu + du, u0), u1);
        const nv = Math.min(Math.max(bv + dv, v0), v1);
        const q = evaluateSurface(data, nu, nv);
        const d = Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]);
        if (d < bd - 1e-12) { bd = d; bu = nu; bv = nv; } else break;
        if (Math.hypot(du, dv) < 1e-9) break;
    }
    const point = evaluateSurface(data, bu, bv);
    return { u: bu, v: bv, point, dist: Math.hypot(point[0] - p[0], point[1] - p[1], point[2] - p[2]) };
}

/** A curve running along `axis` with the other parameter held fixed. */
export function isocurve(data: NurbsSurfaceData, axis: 'u' | 'v', fixed: number, samples = 24): NurbsCurveData {
    const { u, v } = surfaceDomains(data);
    const [lo, hi] = axis === 'u' ? u : v;
    const pts: Pt3[] = [];
    for (let i = 0; i <= samples; i++) {
        const t = lo + ((hi - lo) * i) / samples;
        pts.push(axis === 'u' ? evaluateSurface(data, t, fixed) : evaluateSurface(data, fixed, t));
    }
    return interpolateCurve(pts, Math.min(3, samples)) ?? curveFromControlPoints(pts, 1);
}

/** The four boundary edges as curves: [v-min (U-dir), u-max (V-dir),
 *  v-max (U-dir), u-min (V-dir)] — 3D DupBorder/DupEdge order. */
export function boundaryEdges(data: NurbsSurfaceData): NurbsCurveData[] {
    const { u, v } = surfaceDomains(data);
    return [
        isocurve(data, 'u', v[0]),
        isocurve(data, 'v', u[1]),
        isocurve(data, 'u', v[1]),
        isocurve(data, 'v', u[0]),
    ];
}

/** Display isocurve grid (both directions, interior params). */
export function isoGrid(data: NurbsSurfaceData, count = 6): NurbsCurveData[] {
    const { u, v } = surfaceDomains(data);
    const out: NurbsCurveData[] = [];
    for (let i = 1; i < count; i++) {
        out.push(isocurve(data, 'u', v[0] + ((v[1] - v[0]) * i) / count, 24));
        out.push(isocurve(data, 'v', u[0] + ((u[1] - u[0]) * i) / count, 24));
    }
    return out;
}

/** Ray-cast point-in-polygon in parameter space. */
export function inTrimPolygon(loop: TrimLoopData, u: number, v: number): boolean {
    let inside = false;
    const poly = loop.uv;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i], [xj, yj] = poly[j];
        if ((yi > v) !== (yj > v) && u < ((xj - xi) * (v - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
    }
    return inside;
}

/** True when (u,v) lies in the TRIMMED AWAY region (outer + hole logic). */
export function isTrimmedOut(data: NurbsSurfaceData, u: number, v: number): boolean {
    // Rasterized keep-mask (TrimSrf) takes precedence over polygon trims.
    if (data.trimMask) {
        const { u: [u0, u1], v: [v0, v1] } = surfaceDomains(data);
        const n = data.trimMask.length - 1;
        const i = Math.round(((u - u0) / (u1 - u0 || 1)) * n);
        const j = Math.round(((v - v0) / (v1 - v0 || 1)) * n);
        if (i < 0 || i > n || j < 0 || j > n) return true;
        return !data.trimMask[j][i];
    }
    const trims = data.trims;
    if (!trims || trims.length === 0) return false;
    const outer = trims.find((t) => t.sense === 'outer');
    if (outer && !inTrimPolygon(outer, u, v)) return true;
    return trims.some((t) => t.sense === 'hole' && inTrimPolygon(t, u, v));
}

/**
 * Trim-aware tessellation (the display cache). Samples the exact
 * evaluator on a parameter grid; triangles entirely inside a trimmed
 * region are dropped.
 */
export function tessellateSurface(
    data: NurbsSurfaceData, grade = 24
): { vertices: number[][]; indices: number[][] } {
    const { u, v } = surfaceDomains(data);
    const n = Math.max(2, grade);
    const grid: Pt3[][] = [];
    for (let j = 0; j <= n; j++) {
        const vv = v[0] + ((v[1] - v[0]) * j) / n;
        const row: Pt3[] = [];
        for (let i = 0; i <= n; i++) {
            row.push(evaluateSurface(data, u[0] + ((u[1] - u[0]) * i) / n, vv));
        }
        grid.push(row);
    }
    const alive: boolean[][] = [];
    for (let j = 0; j <= n; j++) {
        const row: boolean[] = [];
        for (let i = 0; i <= n; i++) {
            row.push(!isTrimmedOut(data, u[0] + ((u[1] - u[0]) * i) / n, v[0] + ((v[1] - v[0]) * j) / n));
        }
        alive.push(row);
    }
    const vertices: number[][] = [];
    const indices: number[][] = [];
    const idOf: number[][] = grid.map((row) => row.map(() => -1));
    const vertexAt = (i: number, j: number): number => {
        if (idOf[j][i] >= 0) return idOf[j][i];
        idOf[j][i] = vertices.length;
        vertices.push([...grid[j][i]]);
        return idOf[j][i];
    };
    for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++) {
            const a = alive[j][i], b = alive[j][i + 1], c = alive[j + 1][i + 1], d = alive[j + 1][i];
            if (a && b && c) indices.push([vertexAt(i, j), vertexAt(i + 1, j), vertexAt(i + 1, j + 1)]);
            if (a && c && d) indices.push([vertexAt(i, j), vertexAt(i + 1, j + 1), vertexAt(i, j + 1)]);
        }
    }
    return { vertices, indices };
}

/** Conservative control-net AABB (kernel bounds; enough for zoom/fit). */
export function surfaceBounds(data: NurbsSurfaceData): { min: Pt3; max: Pt3 } {
    const min: Pt3 = [Infinity, Infinity, Infinity];
    const max: Pt3 = [-Infinity, -Infinity, -Infinity];
    data.cv.forEach((row) => row.forEach((p) => {
        min[0] = Math.min(min[0], p[0]); min[1] = Math.min(min[1], p[1]); min[2] = Math.min(min[2], p[2] ?? 0);
        max[0] = Math.max(max[0], p[0]); max[1] = Math.max(max[1], p[1]); max[2] = Math.max(max[2], p[2] ?? 0);
    }));
    return { min, max };
}

/** Build a patch from a control net (rows = V) with clamped uniform knots. */
export function surfaceFromControlNet(
    cv: Pt3[][],
    degreeU = 3,
    degreeV = 3,
    weights?: number[][]
): NurbsSurfaceData | null {
    const rows = cv.length;
    const cols = cv[0]?.length ?? 0;
    if (rows < 2 || cols < 2) return null;
    const dU = Math.max(1, Math.min(degreeU, cols - 1));
    const dV = Math.max(1, Math.min(degreeV, rows - 1));
    const knotsU: number[] = new Array(dU + 1).fill(0);
    for (let i = 1; i <= cols - 1 - dU; i++) knotsU.push(i);
    for (let i = 0; i <= dU; i++) knotsU.push(cols - dU);
    const knotsV: number[] = new Array(dV + 1).fill(0);
    for (let i = 1; i <= rows - 1 - dV; i++) knotsV.push(i);
    for (let i = 0; i <= dV; i++) knotsV.push(rows - dV);
    return {
        degreeU: dU, degreeV: dV, knotsU, knotsV,
        cv: cv.map((r) => r.map((p) => [...p] as Pt3)),
        weights: weights && weights.some((row) => row.some((w) => w !== 1)) ? weights : undefined,
    };
}

/** Planar surface over a closed boundary: bilinear Coons fit of the
 *  boundary ring onto a control net (exact for 3-4 corner rings). */
export function planarSurfaceFromBoundary(boundary: Pt3[]): NurbsSurfaceData | null {
    const n = boundary.length;
    if (n < 3) return null;
    const res = Math.max(4, Math.min(16, n));
    const sampleRing = (t: number): Pt3 => {
        const f = (t * n) % n;
        const i = Math.floor(f);
        const k = f - i;
        const a = boundary[i], b = boundary[(i + 1) % n];
        return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, (a[2] ?? 0) + ((b[2] ?? 0) - (a[2] ?? 0)) * k];
    };
    // degenerate-boundary centroid for the ruled blend
    const cv: Pt3[][] = [];
    for (let j = 0; j <= res; j++) {
        const row: Pt3[] = [];
        for (let i = 0; i <= res; i++) {
            const u0 = i / res, v0 = j / res;
            const e1 = sampleRing(u0);
            const e3 = sampleRing(1 - u0);
            row.push([
                (1 - v0) * e1[0] + v0 * e3[0],
                (1 - v0) * e1[1] + v0 * e3[1],
                (1 - v0) * (e1[2] ?? 0) + v0 * (e3[2] ?? 0),
            ]);
        }
        cv.push(row);
    }
    const srf = surfaceFromControlNet(cv);
    if (srf) srf.planar = true;
    return srf;
}

/**
 * Least-squares surface fit through scattered points (Patch/NetworkSrf/
 * HeightField semantics): bin points onto a u x v grid, average inside
 * bins, complete empty bins by row/col propagation, then a clamped net.
 */
export function fitSurfaceFromPoints(pts: Pt3[], uCount = 8, vCount = 8): NurbsSurfaceData | null {
    if (pts.length < 4) return null;
    const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const acc: number[][][] = Array.from({ length: vCount }, () => Array.from({ length: uCount }, () => [0, 0, 0]));
    const count: number[][] = Array.from({ length: vCount }, () => new Array(uCount).fill(0));
    pts.forEach((p) => {
        const ui = Math.min(uCount - 1, Math.max(0, Math.floor(((p[0] - minX) / (maxX - minX || 1)) * uCount)));
        const vi = Math.min(vCount - 1, Math.max(0, Math.floor(((p[1] - minY) / (maxY - minY || 1)) * vCount)));
        acc[vi][ui][0] += p[0]; acc[vi][ui][1] += p[1]; acc[vi][ui][2] += (p[2] ?? 0);
        count[vi][ui]++;
    });
    const cv: (Pt3 | null)[][] = acc.map((row, vi) => row.map((a, ui) =>
        count[vi][ui] > 0 ? [a[0] / count[vi][ui], a[1] / count[vi][ui], a[2] / count[vi][ui]] as Pt3 : null
    ));
    for (let v = 0; v < vCount; v++) for (let u = 0; u < uCount; u++) {
        if (cv[v][u]) continue;
        const left = u > 0 ? cv[v][u - 1] : null;
        const up = v > 0 ? cv[v - 1]?.[u] : null;
        if (left && up) cv[v][u] = [(left[0] + up[0]) / 2, (left[1] + up[1]) / 2, (left[2] + up[2]) / 2];
        else if (left) cv[v][u] = [...left] as Pt3;
        else if (up) cv[v][u] = [...up] as Pt3;
        else cv[v][u] = [(minX + maxX) / 2, (minY + maxY) / 2, 0];
    }
    return surfaceFromControlNet(cv as Pt3[][]);
}

/**
 * Coons patch from four boundary curves (bottom, right, top, left as
 * point arrays). Exact through the edges, bilinear corner blend inside —
 * the NetworkSrf/EdgeSrf/RailRevolve/Sweep2 substrate.
 */
export function coonsFromBoundaries(
    bottom: Pt3[], right: Pt3[], top: Pt3[], left: Pt3[]
): NurbsSurfaceData | null {
    const nU = 16, nV = 16;
    const sample = (pts: Pt3[], t: number): Pt3 => {
        const f = t * (pts.length - 1);
        const i = Math.min(pts.length - 2, Math.floor(f));
        const k = f - i;
        return [
            pts[i][0] + (pts[i + 1][0] - pts[i][0]) * k,
            pts[i][1] + (pts[i + 1][1] - pts[i][1]) * k,
            (pts[i][2] ?? 0) + ((pts[i + 1][2] ?? 0) - (pts[i][2] ?? 0)) * k,
        ];
    };
    const c00 = bottom[0], c01 = bottom[bottom.length - 1];
    const c10 = left[0], c11 = left[left.length - 1];
    const cv: Pt3[][] = [];
    for (let j = 0; j <= nV; j++) {
        const v = j / nV;
        const row: Pt3[] = [];
        for (let i = 0; i <= nU; i++) {
            const u = i / nU;
            const C1 = sample(bottom, u), C2 = sample(top, u);
            const C3 = sample(left, v), C4 = sample(right, v);
            const Lc: Pt3 = [
                (1 - u) * C3[0] + u * C4[0],
                (1 - u) * C3[1] + u * C4[1],
                (1 - u) * (C3[2] ?? 0) + u * (C4[2] ?? 0),
            ];
            const Bc: Pt3 = [
                (1 - v) * C1[0] + v * C2[0],
                (1 - v) * C1[1] + v * C2[1],
                (1 - v) * (C1[2] ?? 0) + v * (C2[2] ?? 0),
            ];
            const corner: Pt3 = [
                (1 - u) * (1 - v) * c00[0] + u * (1 - v) * c01[0] + (1 - u) * v * c10[0] + u * v * c11[0],
                (1 - u) * (1 - v) * c00[1] + u * (1 - v) * c01[1] + (1 - u) * v * c10[1] + u * v * c11[1],
                (1 - u) * (1 - v) * (c00[2] ?? 0) + u * (1 - v) * (c01[2] ?? 0) + (1 - u) * v * (c10[2] ?? 0) + u * v * (c11[2] ?? 0),
            ];
            row.push([
                Lc[0] + Bc[0] - corner[0],
                Lc[1] + Bc[1] - corner[1],
                (Lc[2] ?? 0) + (Bc[2] ?? 0) - (corner[2] ?? 0),
            ]);
        }
        cv.push(row);
    }
    return surfaceFromControlNet(cv);
}

/**
 * Loft/sweep surface through ordered section curves: resample each to the
 * common CV count, interpolate a curve per rail-direction column, then
 * skin those rails into a control net. Produces a real B-spline surface
 * passing (interpolating) through every section.
 */
export function loftThroughSections(sections: Pt3[][], samples = 20): NurbsSurfaceData | null {
    const valid = sections.filter((s) => s.length >= 2);
    if (valid.length < 2) return null;
    const resampled = valid.map((s) => {
        const out: Pt3[] = [];
        for (let i = 0; i <= samples; i++) {
            const t = (i / samples) * (s.length - 1);
            const j = Math.min(s.length - 2, Math.floor(t));
            const k = t - j;
            out.push([
                s[j][0] + (s[j + 1][0] - s[j][0]) * k,
                s[j][1] + (s[j + 1][1] - s[j][1]) * k,
                (s[j][2] ?? 0) + ((s[j + 1][2] ?? 0) - (s[j][2] ?? 0)) * k,
            ]);
        }
        return out;
    });
    // interpolate a rail through each station index, then rows of the net
    const rails: Pt3[][] = [];
    for (let i = 0; i <= samples; i++) {
        rails.push(resampled.map((sec) => sec[i]));
    }
    const net: Pt3[][] = [];
    const step = Math.max(1, Math.floor(samples / 12));
    for (let i = 0; i < rails.length; i += step) {
        net.push(rails[i]);
    }
    if (net[net.length - 1] !== rails[rails.length - 1]) net.push(rails[rails.length - 1]);
    return surfaceFromControlNet(net);
}
